// Decodes an audio file into a compact peak array so the timeline can render
// a waveform that actually reflects the clip's content — the previous
// Waveform component painted a fixed sine pattern for every audio clip,
// making split pieces of the same file visually indistinguishable.

const PEAK_RESOLUTION = 600; // samples across the full source duration

let sharedCtx = null;
function getAudioContext() {
  if (sharedCtx) return sharedCtx;
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  sharedCtx = new AudioCtx();
  return sharedCtx;
}

function computePeaks(audioBuffer, resolution) {
  const channelData = audioBuffer.getChannelData(0);
  const blockSize = Math.max(1, Math.floor(channelData.length / resolution));
  const peaks = new Float32Array(resolution);
  for (let i = 0; i < resolution; i++) {
    const start = i * blockSize;
    let max = 0;
    for (let j = 0; j < blockSize && start + j < channelData.length; j++) {
      const v = Math.abs(channelData[start + j]);
      if (v > max) max = v;
    }
    peaks[i] = max;
  }
  return peaks;
}

// url -> Promise<{ peaks: Float32Array, duration: number } | null>
const _peaksCache = new Map();

export function getAudioPeaks(url) {
  if (!url) return Promise.resolve(null);
  if (_peaksCache.has(url)) return _peaksCache.get(url);

  // credentials: "include" — matches every other gated-asset fetch in the
  // editor (withSidecarSuffix call sites); needed for the cookie-mode asset
  // gate since this is a cross-origin (8080 -> 4000) fetch() call, which
  // won't send the aidea_asset cookie by default.
  const promise = fetch(url, { credentials: "include" })
    .then((res) => res.arrayBuffer())
    .then((buf) => {
      const ctx = getAudioContext();
      if (!ctx) return null;
      return ctx.decodeAudioData(buf).then((audioBuffer) => ({
        peaks: computePeaks(audioBuffer, PEAK_RESOLUTION),
        duration: audioBuffer.duration,
      }));
    })
    .catch(() => null);

  _peaksCache.set(url, promise);
  return promise;
}
